// Control de PAUSA de mensajes automáticos del negocio (F3, 9-sep-2026).
// Plan: /root/forobeta-ops/docs/plan-pausa-envios-whatsapp.md
//
// Mismo control que ya tiene la agenda dental, aquí para lealtad: los mensajes que
// más salen solos en este vertical son los PUNTOS de una visita, los cumpleaños,
// las gift cards y las campañas. Sin este botón, el negocio harto de mensajes solo
// tiene una salida: desvincular su WhatsApp — y eso apaga también lo que sí quería
// que saliera, y lo deja mudo hasta que alguien reescanee un QR.
import React, { useCallback, useEffect, useState } from 'react';
import { PauseCircle, PlayCircle, Loader2 } from 'lucide-react';
import whatsappApi from '../../../api/whatsappApi';
import {
  leerPausa, notifyWaPauseChanged, hastaTexto, DURACIONES, WA_PAUSE_EVENT,
  PAUSE_OFF, type WaPauseState,
} from '../waPauseShared';

const WaPauseCard: React.FC = () => {
  const [st, setSt] = useState<WaPauseState>(PAUSE_OFF);
  const [level, setLevel] = useState<1 | 2>(2);
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => { leerPausa().then(setSt); }, []);
  useEffect(() => {
    cargar();
    window.addEventListener(WA_PAUSE_EVENT, cargar);
    return () => window.removeEventListener(WA_PAUSE_EVENT, cargar);
  }, [cargar]);

  const pausar = async () => {
    setBusy(true); setError(null);
    try { setSt(await whatsappApi.setPause(level, minutes)); notifyWaPauseChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo pausar'); }
    finally { setBusy(false); }
  };

  const reanudar = async () => {
    setBusy(true); setError(null);
    try { await whatsappApi.clearPause(); setSt(PAUSE_OFF); notifyWaPauseChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo reanudar'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-surface-1 border border-edge rounded-2xl p-6">
      <div className="flex items-start gap-3">
        <PauseCircle className={`w-5 h-5 mt-0.5 ${st.activa ? 'text-amber-500' : 'text-content-3'}`} />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-content-1">Pausar mensajes automáticos</h2>
          <p className="mt-1 text-sm text-content-2">
            Para cuando estás cargando datos viejos o cuadrando cuentas y no quieres que a tus
            clientes les llegue nada. <strong>Los mensajes que envías tú desde el panel siguen
            saliendo</strong>, y tu WhatsApp sigue conectado: no hace falta desvincular nada.
          </p>
        </div>
      </div>

      {st.activa ? (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {st.level === 1 ? 'Campañas y masivos' : 'Todo lo automático'} en pausa {hastaTexto(st.until)}
          </p>
          <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300/90">
            Se reanuda solo al cumplirse la hora. Nada de lo pausado se manda después.
          </p>
          <button
            onClick={reanudar}
            disabled={busy}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-edge px-3 py-2 text-sm font-medium text-content-1 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Reanudar ahora
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-content-2 mb-1">Qué se calla</label>
            <select
              value={level}
              onChange={(e) => setLevel(Number(e.target.value) === 1 ? 1 : 2)}
              className="w-full rounded-lg border border-edge bg-surface-2 px-3 py-2 text-sm text-content-1"
            >
              <option value={2}>Todo lo automático (puntos, cumpleaños, gift cards, campañas)</option>
              <option value={1}>Solo campañas y avisos masivos</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-content-2 mb-1">Por cuánto tiempo</label>
            <div className="flex gap-2">
              {DURACIONES.map((d) => (
                <button
                  key={d.minutes}
                  onClick={() => setMinutes(d.minutes)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    minutes === d.minutes
                      ? 'border-amber-400 bg-amber-50 font-semibold text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
                      : 'border-edge text-content-2 hover:bg-surface-2'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={pausar}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-edge px-3 py-2 text-sm font-medium text-content-1 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />}
            Pausar
          </button>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default WaPauseCard;
